import { prefillConnection } from '@/adapter/applicationLoader';
import { Application } from '@/types/application';
import { Connection } from '@/types/connection';
import { Detail } from '@/types/detail';
import { Block, Configuration, Metadata } from '@/types/metadata/metadata';
import { Subroutine } from '@/types/subroutine';
import { Variable, VariableScope } from '@/types/variable';
import { getHighestNumber } from '@/util/number';
import { XYPosition } from '@xyflow/react';
import { ConfirmOptions, ConfirmResult } from 'material-ui-confirm';
import { arityScope, isArityBound, listArity, padToArity } from './arity';
import { findAppStartNodes } from './blocks';
import { deleteBlock } from './delete';
import { isArray, isConnection, isNullOrUndefined, isString } from './typeGuards';

export const APPLICATION_FORMAT = 5;

export const findSubroutine = (subroutines: Subroutine[], subroutineId: number | undefined): Subroutine | undefined =>
  subroutines.find((subroutine) => subroutine.id === subroutineId);

export const getSubroutineByNode = (application: Application, blockId: string): Subroutine | undefined => {
  return application.subroutines.find((subroutine) => subroutine.nodes.includes(parseInt(blockId)));
};

export const findVariableWithScope = (
  variableName: string,
  subroutineId: number,
  scope: VariableScope,
  application: Application,
  metadata: Metadata
) => {
  if (scope === 'local') {
    return application.variables.local[subroutineId]?.find((variable) => variable.name === variableName);
  } else {
    const varInApp = application.variables[scope]?.find((varInScope) => varInScope.name === variableName);
    const varInMeta = metadata.variables?.[scope]?.find((varInMetaScope) => varInMetaScope.name === variableName);
    return varInApp || varInMeta;
  }
};

export const findVariable = (
  subroutineId: number,
  variableName: string,
  application: Application,
  metadata: Metadata
) => {
  if (!variableName) return;
  const local = application.variables.local;
  const variableList = local && local[subroutineId];
  if (variableList && !isString(variableList)) {
    const foundVariable = variableList.find((variables) => variables.name === variableName);
    if (foundVariable) return foundVariable;
  }

  let varInApp: Variable | undefined = undefined;
  Object.entries(application.variables).forEach(([, variables]) => {
    if (Array.isArray(variables)) {
      const foundVar = variables.find((variable) => variable.name === variableName);
      varInApp = foundVar || varInApp;
    }
  });

  let varInMeta: Variable | undefined = undefined;
  metadata.variables &&
    Object.entries(metadata.variables).forEach(([, variables]) => {
      if (Array.isArray(variables)) {
        varInMeta = variables.find((variable) => variable.name === variableName) || varInMeta;
      }
    });

  return varInApp || varInMeta;
};

export const getUpdatedNodes = (value: number | string, type: string, details: Record<string, Detail>): string[] => {
  const foundNodes: string[] = [];
  Object.entries(details).forEach(([nodeId, detail]) => {
    const detailValue = detail[type];
    if (isArray(detailValue) ? detailValue.includes(value) : detailValue === value) {
      foundNodes.push(nodeId);
    }
  });

  return foundNodes;
};

export const getHighestConnectionId = (connections: Connection[]) => {
  const onlyConnections: Connection[] = connections.filter(isConnection);
  return getHighestNumber(onlyConnections, 'id');
};

export const sortBy = (sortBy: string, items: any[], invert?: boolean) => {
  return invert
    ? items.toSorted((a, b) => (a[sortBy] && b[sortBy] ? b[sortBy].localeCompare(a[sortBy]) : 1))
    : items.toSorted((a, b) => (a[sortBy] && b[sortBy] ? a[sortBy].localeCompare(b[sortBy]) : 1));
};

export const applicationSearchOptions = [
  { name: 'label', label: 'Label' },
  { name: 'name', label: 'Name' },
  { name: 'revisionCreated', label: 'Updated' }
];

export const applicationCustomTooltip = [
  { name: 'revisionCreated', tooltip: { inverted: 'Sort New-Old', upright: 'Sort Old-New' } }
];

const applicationValues = {
  blocks: {
    connections: {},
    details: {},
    layouts: {},
    nodes: {},
    portoptions: {}
  },
  variables: {
    config: [],
    global: [],
    task: [],
    channel: [],
    session: [],
    local: {}
  },
  prompts: [],
  promptGroups: [],
  semaphores: []
};

export const newApplication: Application = {
  name: '',
  label: 'New Application',
  generated: new Date().toISOString(),
  format: APPLICATION_FORMAT,
  subroutines: [
    {
      id: 1,
      startblock: undefined,
      args: [],
      arg_bindings: [],
      label: 'Start',
      nodes: [],
      return_types: []
    }
  ],
  ...applicationValues
};

export const emptyApplication: Application = {
  name: '',
  label: '',
  generated: '',
  format: APPLICATION_FORMAT,
  subroutines: [],
  ...applicationValues
};

export const getNewBlockId = (application: Application): number => {
  const nodeIds = Object.keys(application.blocks.nodes).map((id) => Number.parseInt(id));
  const highestId = Math.max(...nodeIds, 0);
  return highestId + 1;
};

// Points the subroutine at the new startblock, deleting the old one if it has one.
const replaceStartblock = (newId: number, application: Application, subroutineId: number): Application => {
  const sub = findSubroutine(application.subroutines, subroutineId);
  if (!sub) return application;

  if (!sub.startblock) {
    sub.startblock = newId;
    return application;
  }

  // deleteBlock clears the whole interface with the old startblock, so it is carried across by hand.
  const args = sub.args;
  const argBindings = sub.arg_bindings;
  const eventArgs = sub.event_args;
  const eventArgBindings = sub.event_arg_bindings;
  const returnTypes = sub.return_types;
  const deleted = deleteBlock(application, subroutineId, sub.startblock);

  return {
    ...deleted,
    subroutines: deleted.subroutines.map((subroutine) =>
      subroutine.id === subroutineId
        ? {
            ...subroutine,
            args: args,
            arg_bindings: argBindings,
            event_args: eventArgs,
            event_arg_bindings: eventArgBindings,
            return_types: returnTypes,
            startblock: newId
          }
        : subroutine
    )
  };
};

// Sets or replaces the start block of a subroutine, asking before replacing. Never resolves on cancel.
export const setOrReplaceStartblock = (
  newId: number,
  application: Application,
  selectedSubroutineId: number,
  confirm: (options?: ConfirmOptions | undefined) => Promise<ConfirmResult>
): Promise<Application> => {
  return new Promise((resolve) => {
    const sub = findSubroutine(application.subroutines, selectedSubroutineId);
    if (!sub) return;

    if (!sub.startblock) {
      return resolve(replaceStartblock(newId, application, selectedSubroutineId));
    }

    confirm({
      title: 'Replace startblock?',
      description: 'This subroutine already has a startblock. Do you want to replace it with this one?',
      confirmationText: 'Replace'
    }).then((result) => {
      if (!result.confirmed) return;
      resolve(replaceStartblock(newId, application, selectedSubroutineId));
    });
  });
};

/**
 * The appstart variant of setOrReplaceStartblock. An application may only hold one appstart block, so one
 * that exists in another subroutine is replaced as well - behind the same single confirmation, so the user
 * is only ever asked once. Never resolves on cancel, like setOrReplaceStartblock.
 */
export const setOrReplaceAppStartblock = (
  newId: number,
  application: Application,
  metadata: Metadata,
  selectedSubroutineId: number,
  confirm: (options?: ConfirmOptions | undefined) => Promise<ConfirmResult>
): Promise<Application> => {
  return new Promise((resolve) => {
    const sub = findSubroutine(application.subroutines, selectedSubroutineId);
    if (!sub) return;

    const existing = findAppStartNodes(application.blocks.nodes, metadata).find((nodeId) => nodeId !== sub.startblock);
    if (isNullOrUndefined(existing)) {
      return resolve(setOrReplaceStartblock(newId, application, selectedSubroutineId, confirm));
    }

    const type = application.blocks.nodes[existing].type;
    confirm({
      title: `Replace ${type}?`,
      description: sub.startblock
        ? `A ${type} block already exists on this application, and this subroutine already has a startblock. Do you want to replace both with this one?`
        : `A ${type} block already exists on this application. Do you want to replace it with this one?`,
      confirmationText: 'Replace'
    }).then((result) => {
      if (!result.confirmed) return;
      const owner = getSubroutineByNode(application, existing.toString());
      const cleared = deleteBlock(structuredClone(application), owner?.id ?? selectedSubroutineId, existing);
      resolve(replaceStartblock(newId, cleared, selectedSubroutineId));
    });
  });
};

export const addBlock = (
  metadata: Metadata,
  application: Application,
  subroutineId: number,
  metaBlock: Block,
  position: XYPosition,
  label?: string
): { application: Application; newId: number } => {
  const newApplication = structuredClone(application);

  //Create unique ID
  const newId = getNewBlockId(application);

  // Add block to nodes
  newApplication.blocks.nodes[newId] = {
    label: label || '',
    type: metaBlock.type
  };

  // Let subroutine know this block is meant to be in it
  newApplication.subroutines.find((subroutine) => subroutine.id === subroutineId)?.nodes.push(newId);

  const defaultValues: Detail = {};
  metaBlock.configuration.forEach((config) => {
    if (config.default !== null) {
      defaultValues[config.value] = config.default;
    }
  });

  // An arity bound list starts at the arity of its interface, so a block against an empty interface is complete.
  (metaBlock.configuration as Configuration[]).filter(isArityBound).forEach((config) => {
    const arity = listArity(config, defaultValues, newId, arityScope(newApplication, metadata));
    if (!isNullOrUndefined(arity)) {
      defaultValues[config.value] = padToArity([], arity);
    }
  });

  newApplication.blocks.details[newId] = defaultValues;
  newApplication.blocks.layouts = { ...newApplication.blocks.layouts, [newId]: { position: position } };

  // Fill in any potential connection with missing data
  const prefilled = prefillConnection(newApplication, metadata, newId.toString(), metaBlock);

  addToHistory(metaBlock);
  return { application: prefilled || newApplication, newId };
};

const historyKey = 'history.usedBlocks';
const maxHistorySize = 10;

/**
 * Retrieves the block history from localStorage and returns up to the specified amount.
 *
 * @param {number} [amount=maxHistorySize] - The maximum number of blocks to return. Defaults to maxHistorySize.
 * @returns {Block[]} An array containing the block history, limited to the specified amount.
 */
export const getBlockHistory = (amount: number = maxHistorySize): Block[] => {
  const historyObj = localStorage.getItem(historyKey);
  if (historyObj == null) {
    localStorage.setItem(historyKey, JSON.stringify([]));
    return [];
  }
  const parsedHistory = JSON.parse(historyObj);
  const history = isArray(parsedHistory) ? parsedHistory : [];

  return history.slice(0, amount);
};

/**
 * Adds a new block to the block history in localStorage.
 * If a block of the same type already exists, it moves to the beginning.
 * The history is also limited to a maximum size defined by maxHistorySize.
 *
 * @param {Block} block - The new block to add to the history.
 * @returns {Block[]} The updated block history.
 */
export const addToHistory = (block: Block): Block[] => {
  const historyObj = localStorage.getItem(historyKey);

  const existingHistory: Block[] = historyObj ? JSON.parse(historyObj) : [];

  // Remove existing block of the same type, if present
  const filteredHistory = isArray(existingHistory)
    ? existingHistory.filter((existingBlock) => existingBlock.type !== block.type)
    : [];

  // Should be sorted by last used block first
  const newHistory = [block, ...filteredHistory];

  const limitedHistory = newHistory.length > maxHistorySize ? newHistory.slice(0, maxHistorySize) : newHistory;

  localStorage.setItem(historyKey, JSON.stringify(limitedHistory));

  return limitedHistory;
};

export const updateConnection = (source: string, value: Connection | Connection[], application: Application) => {
  const newApplication = {
    ...application,
    blocks: {
      ...application.blocks,
      connections: {
        ...application.blocks.connections,
        [source]: !isArray(value)
          ? application.blocks.connections[source].map((conn) => (conn.id === value.id ? value : conn))
          : value
      }
    }
  };

  return newApplication;
};

export type ConnectionRef = {
  source: string;
  connectionId: number;
};

export const redirectConnections = (moves: ConnectionRef[], dst: number | null, application: Application) => {
  const idsBySource = moves.reduce((grouped: Record<string, Set<number>>, move) => {
    if (!grouped[move.source]) {
      grouped[move.source] = new Set<number>();
    }
    grouped[move.source].add(move.connectionId);
    return grouped;
  }, {});

  const connections = { ...application.blocks.connections };

  Object.entries(idsBySource).forEach(([source, connectionIds]) => {
    const sourceConnections = application.blocks.connections[source];
    if (!sourceConnections) return;

    connections[source] = sourceConnections.map((connection) =>
      isConnection(connection) && connectionIds.has(connection.id) ? { ...connection, dst: dst } : connection
    );
  });

  return {
    ...application,
    blocks: {
      ...application.blocks,
      connections: connections
    }
  };
};

export const removeConnection = (source: string, id: number, application: Application) => {
  const newApplication = {
    ...application,
    blocks: {
      ...application.blocks,
      connections: {
        ...application.blocks.connections,
        [source]: application.blocks.connections[source].filter((conn) => conn.id !== id)
      }
    }
  };

  return newApplication;
};

export const updateDetail = (
  nodeId: string | string[],
  target: string,
  value: unknown | unknown[],
  application: Application
) => {
  const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];
  const values = Array.isArray(value) ? value : nodeIds.map(() => value);

  const updatedDetails = { ...application.blocks.details };
  nodeIds.forEach((id, index) => {
    updatedDetails[id] = {
      ...updatedDetails[id],
      [target]: values[index]
    };
  });

  return {
    ...application,
    blocks: {
      ...application.blocks,
      details: updatedDetails
    }
  };
};

export const updatePositions = (nodeIds: string[], positions: XYPosition[], application: Application): Application => {
  const updatedLayouts = { ...application.blocks.layouts };
  nodeIds.forEach((id, index) => {
    updatedLayouts[id] = { ...updatedLayouts[id], position: positions[index] };
  });

  return {
    ...application,
    blocks: {
      ...application.blocks,
      layouts: updatedLayouts
    }
  };
};

export const updateDetails = (nodeId: string, value: Detail, application: Application) => {
  if (!application.blocks.details[nodeId]) {
    application.blocks.details[nodeId] = {};
  }
  const newApplication = {
    ...application,
    blocks: {
      ...application.blocks,
      details: {
        ...application.blocks.details,
        [nodeId]: value
      }
    }
  };

  return newApplication;
};

export const updateNode = (nodeId: number, target: string, value: unknown, application: Application) => {
  const newApplication = {
    ...application,
    blocks: {
      ...application.blocks,
      nodes: {
        ...application.blocks.nodes,
        [nodeId]: {
          ...application.blocks.nodes[nodeId],
          [target]: value
        }
      }
    }
  };

  return newApplication;
};
